/**
 * Score normalization utilities
 * Converts raw cosine similarity scores (0.15-0.70) to user-friendly display scores (65-100)
 */

/**
 * Normalizes a raw search score to a display-friendly percentage
 * 
 * @param rawScore - Raw cosine similarity score from search (typically 0.15-0.70)
 * @returns Normalized score for display (65-100)
 */
export function normalizeScore(rawScore: number): number {
  // Expected score range from search engine (Pinecone cosine similarity)
  const MIN_EXPECTED = 0.15;  // Lowest relevant score
  const MAX_EXPECTED = 0.70;  // Highest typical score
  
  // Display range for UI
  const MIN_DISPLAY = 65;     // Show minimum as 65
  const MAX_DISPLAY = 100;    // Show maximum as 100
  
  // Linear mapping from expected range to display range
  const normalized = ((rawScore - MIN_EXPECTED) / (MAX_EXPECTED - MIN_EXPECTED)) 
                     * (MAX_DISPLAY - MIN_DISPLAY) + MIN_DISPLAY;
  
  // Clamp between min/max to handle outliers
  return Math.min(Math.max(normalized, MIN_DISPLAY), MAX_DISPLAY);
}

/**
 * Formats a score for display as a whole number
 * 
 * @param score - Score to format (raw or normalized)
 * @param normalize - Whether to normalize the score first (default: true)
 * @returns Formatted score string (e.g., "82")
 */
export function formatScore(score: number, normalize: boolean = true): string {
  const displayScore = normalize ? normalizeScore(score) : score;
  return displayScore.toFixed(0);
}

